import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { InventoryService } from '@app/shared/services/inventory.service';
import { ActivatedRoute, Router } from '@angular/router';
import * as XLSX from 'xlsx';  // Import SheetJS

@Component({
  selector: 'app-inventory-list',
  templateUrl: './inventory-list.component.html',
  styleUrls: ['./inventory-list.component.css']
})
export class InventoryListComponent implements OnInit {
  inventoryMaterials: any[] = [];
  categories: string[] = []; // Array to store unique categories
  codePrefix: string = '30';  // Default code prefix for categories
  descriptionPrefix: string = 'RAW MATERIALS';  // Default description for categories
  subcategories: string[] = []; // Array to store subcategories for a selected category
  showSubcategoriesForCategory: string | null = null; // To track which category's subcategories are visible
  detailsForSubcategory: any[] = []; // Store details for selected subcategory
  selectedCategory: string = ''; // To store the selected category
  viewDetailsMode: boolean = false; // Flag to switch between table views

    
   
    isPopupVisible: boolean = false;
  
  constructor(
    private inventoryService: InventoryService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private acRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.fetchInventoryMaterials();
  }

  fetchInventoryMaterials() {
    this.inventoryService.getAllInventry().subscribe(
      (data: any[]) => {
        this.inventoryMaterials = data;
        this.categories = this.extractUniqueCategories(data);
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error fetching inventory materials:', error);
      }
    );
  }

  // Extract unique categories
  extractUniqueCategories(data: any[]): string[] {
    const categories: string[] = [];
    data.forEach(item => {
      if (!categories.includes(item.category)) {
        categories.push(item.category);
      }
    });
    return categories.sort();
  }

  // When "Next" is clicked, show subcategories for the selected category
  onNextButtonClick(category: string) {
    this.selectedCategory = category; 
    this.showSubcategoriesForCategory = category;  // Set the category to show subcategories
    this.fetchSubcategories(category);  // Fetch subcategories for the selected category
  }

  // Fetch subcategories for the selected category
  fetchSubcategories(category: string) {
    this.subcategories = this.extractUniqueSubcategories(this.inventoryMaterials, category);
    this.cdr.detectChanges();
  }

  // Extract unique subcategories for the selected category
  extractUniqueSubcategories(data: any[], selectedCategory: string): string[] {
    const subcategories: string[] = [];
    data.forEach(item => {
      if (item.category === selectedCategory && !subcategories.includes(item.subcategory)) {
        subcategories.push(item.subcategory);
      }
    });
    return subcategories.sort();
  }

  calculateTotalInput(categoryOrSubcategory: string): number {
    return this.inventoryMaterials
      .filter(item => item.category === categoryOrSubcategory || item.subcategory === categoryOrSubcategory)
      .reduce((acc, item) => acc + (item.qty || 0), 0);
  }

  calculateTotalOutput(categoryOrSubcategory: string): number {
    return this.inventoryMaterials
      .filter(item => item.category === categoryOrSubcategory || item.subcategory === categoryOrSubcategory)
      .reduce((acc, item) => acc + (item.output || 0), 0);
  }

  calculateTotalBalance(categoryOrSubcategory: string): number {
    const totalInput = this.calculateTotalInput(categoryOrSubcategory);
    const totalOutput = this.calculateTotalOutput(categoryOrSubcategory);
    return totalInput - totalOutput;
  }

  // View details for the selected subcategory
  viewDetails(subcategory: string) {
    this.viewDetailsMode = true; // Switch to view details mode
    this.fetchDetailsForSubcategory(subcategory); // Fetch details for this subcategory
  }

  // Fetch detailed data for the selected subcategory
  fetchDetailsForSubcategory(subcategory: string) {
    this.detailsForSubcategory = this.inventoryMaterials.filter(item => item.subcategory === subcategory);
  }

  // Go back to the initial stock view (categories list)
  goBack() {
    this.showSubcategoriesForCategory = null; // Hide subcategories
    this.viewDetailsMode = false; // Switch back to category view
  }

  // Go back from the details view
  onBackFromDetails() {
    this.viewDetailsMode = false; // Switch back to subcategory view
  }

    // Function to export the data to Excel
    exportToExcel(): void {
      const dataToExport: any[] = [];
    
      // Case 1: When viewing the main categories or subcategories without 'View Details' mode
      if (this.showSubcategoriesForCategory === null || !this.viewDetailsMode) {
        if (this.showSubcategoriesForCategory === null) {
          // Export all inventory details without categorization
          this.inventoryMaterials.forEach(item => {
            const detailData = {
              'Category': item.category,
              'Subcategory': item.subcategory,
              'Material': item.materialDescription,
              'Qty Supplied': item.qty,
              'Qty Accepted': item.qtyAccepted,
              'Balance': item.qty - item.qtyAccepted,
              'Date': item.receivedDate,
              'Group': item.supplier
            };
            dataToExport.push(detailData);
          });
        } 
        // Export subcategories details
        else {
          const selectedCategory = this.showSubcategoriesForCategory;
          const subcategories = this.extractUniqueSubcategories(this.inventoryMaterials, selectedCategory);
    
          subcategories.forEach(subcategory => {
            // Export detailed material records for each subcategory
            const subcategoryDetails = this.inventoryMaterials.filter(item => item.subcategory === subcategory);
    
            subcategoryDetails.forEach(detail => {
              const detailData = {
                'Category': selectedCategory,
                'Subcategory': subcategory,
                'Material': detail.materialDescription,
                'Qty Supplied': detail.qty,
                'Qty Accepted': detail.qtyAccepted,
                'Balance': detail.qty - detail.qtyAccepted,
                'Date': detail.receivedDate,
                'Group': detail.supplier
              };
              dataToExport.push(detailData);
            });
          });
        }
      }
    
      // Case 2: When viewing details for a specific subcategory
      if (this.viewDetailsMode) {
        // For the "View Details" mode, export the specific details for the selected subcategory
        this.detailsForSubcategory.forEach(detail => {
          const detailData = {
            'Date': detail.receivedDate,
            'Group': detail.supplier,
            'Material': detail.materialTypeGrade,
            'Description': detail.materialDescription,
            'Qty Supplied': detail.qty,
            'Qty Accepted': detail.qtyAccepted,
            'Balance': detail.qty - detail.qtyAccepted
          };
          dataToExport.push(detailData);
        });
      }
    
      // Create a new worksheet from the data
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Inventory Details');
    
      // Trigger download of the Excel file
      XLSX.writeFile(wb, 'inventory-report.xlsx');
    }
    
    openPopup() {
        this.isPopupVisible = true;
      }
      
      closePopup() {
        this.isPopupVisible = false;
      }
    
    
}







// export class InventoryListComponent implements OnInit {

//   inventoryMaterials: any[] = [];
//   totalBalance: number = 0;
//   totalInput: number = 0;
//   totalOutput: number = 0;
//   codePrefix : string = '30';
//   descriptionPrefix: string = 'RAW MATERIALS';
//   categories: string[] = []; // Array to store unique categories
//   showReportTable: boolean = false;
//   viewCategoryData: any[] = [];
//   selectedViewCategory: string = '';
//   isPopupVisible: boolean = false;
//   selectedCategory: string;
//   subcategories: string[] = [];
//   constructor(
//     private inventoryService: InventoryService,
//     private cdr: ChangeDetectorRef, // Inject ChangeDetectorRef
//     private router: Router, // Inject Router
//     private acRoute: ActivatedRoute,

//   ) { }

//   ngOnInit(): void {
//     this.acRoute.queryParams.subscribe(params => {
//       this.selectedCategory = params['category'];
//       console.log(this.selectedCategory);
//       // Call a function to fetch subcategories based on the selected category
//       this.fetchSubcategories(this.selectedCategory);
//        // Manually trigger change detection
//        this.cdr.detectChanges();
//     });
//     this.fetchInventoryMaterials();
//   }

//   fetchSubcategories(selectedCategory: string) {
//     this.inventoryService.getAllInventry().subscribe(
//       (data: any[]) => {
//         // Assign data to inventoryMaterials
//         this.inventoryMaterials = data;
//         // Extract unique subcategories for the selected category
//         this.subcategories = this.extractUniqueSubcategories(data, selectedCategory);
//         // Manually trigger change detection
//         this.cdr.detectChanges();
//       },
//       (error) => {
//         console.error('Error fetching inventory materials:', error);
//       }
//     );
//   }
  

//   extractUniqueSubcategories(data: any[], selectedCategory: string): string[] {
//     const subcategories: string[] = [];
//     data.forEach(item => {
//       if (item.category === selectedCategory && !subcategories.includes(item.subcategory)) {
//         subcategories.push(item.subcategory);
//       }
//     });
//     return subcategories.sort();
//   }

//   fetchInventoryMaterials() {
//     this.inventoryService.getAllInventry().subscribe(
//       (data: any[]) => {
//         // Assign data to inventoryMaterials
//         this.inventoryMaterials = data;
//         // Extract unique categories and subcategories
//         this.categories = this.extractUniqueCategories(data);
     
//         // Manually trigger change detection
//         this.cdr.detectChanges();
//       },
//       (error) => {
//         console.error('Error fetching inventory materials:', error);
//       }
//     );
//   }

//   extractUniqueCategories(data: any[]): string[] {
//     const categories: string[] = [];
//     data.forEach(item => {
//       if (!categories.includes(item.category)) {
//         categories.push(item.category);
//       }
//     });
//     return categories.sort();
//   }

//   calculateTotalInput(categoryOrSubcategory: string): number {
//     return this.inventoryMaterials
//       .filter(item => item.category === categoryOrSubcategory || item.subcategory === categoryOrSubcategory)
//       .reduce((acc, item) => acc + (item.qty || 0), 0);
//   }

//   calculateTotalOutput(categoryOrSubcategory: string): number {
//     return this.inventoryMaterials
//       .filter(item => item.category === categoryOrSubcategory || item.subcategory === categoryOrSubcategory)
//       .reduce((acc, item) => acc + (item.output || 0), 0);
//   }

//   calculateTotalBalance(categoryOrSubcategory: string): number {
//     const totalInput = this.calculateTotalInput(categoryOrSubcategory);
//     const totalOutput = this.calculateTotalOutput(categoryOrSubcategory);
//     return totalInput - totalOutput;
//   }

//   viewDetails(category: string) {
//     console.log('Viewing details of category:', category);
//     this.showReportTable = true; // Set the flag to show the second table
//     this.selectedViewCategory = category; // Store the selected category
  
//     // Filter existing data based on the selected category
//     this.viewCategoryData = this.inventoryMaterials.filter(item => item.category === category);
  
//     // Calculate the total input for the selected category
//     this.calculateTotal(category);
//   }
  
//   calculateTotal(category: string) {
//     // Initialize variables to hold the total input, output, and balance
//     let totalInput = 0;
//     let totalOutput = 0;
//     let initialStock = 0;
  
//     // Filter existing data based on the selected category
//     const categoryData = this.inventoryMaterials.filter(item => item.category === category);
  
//     // Loop through each item in the category data
//     categoryData.forEach(item => {
//       // Calculate the initial stock as the sum of quantities supplied
//       initialStock += item.qty || 0;
  
//       // Calculate the total output as the sum of quantities accepted
//       totalOutput += item.qty_accepted || 0;
//     });
  
//     // Calculate the total input as the sum of initial stock and total output
//     totalInput = initialStock + totalOutput;
  
//     // Calculate the balance as the difference between total input and total output
//     this.totalBalance = totalInput - totalOutput;
//     this.totalInput = totalInput;
//     this.totalOutput = totalOutput;
//   }
  

// onNextButtonClick(category: string) {
//   // Perform action for the next button click for the specified category
//   console.log('Next button clicked for category:', category);
//   // Navigate to the next component and pass the selected category as a parameter
//   this.router.navigate(['/inventory'], { queryParams: { category: category } });
// }
// openPopup() {
//   this.isPopupVisible = true;
// }

// closePopup() {
//   this.isPopupVisible = false;
// }
// }
